import { agentStaleLevel, type AgentStaleLevel } from '@infras/shared-types';

/**
 * Fleet-list derivations that have to know about BOTH the clock and the status column.
 *
 * ONE CLOCK, TWO STATES (owner decision, Binh, 2026-08-25). An agent that has not
 * heartbeat for more than 10 minutes is a warning; more than 15 minutes is red and
 * DevOps acts on it from the Overview checklist. There is no third state and no
 * second timescale: the 4-hour "gone" concept — a separate clock that decided when an
 * address became reclaimable — is gone from this app. Everything a screen says about
 * an agent's silence now comes from `agentStaleLevel`, which is one function with one
 * pair of thresholds.
 *
 * The distinction this file still exists to encode, after the first production bulk
 * removal (2026-08-24: beta's 14 dead agents were removed and the fleet still read
 * "21 agents · 14 gone" on a hard reload):
 *
 *   red     answers  has the NODE stopped reporting?  — the silence clock
 *   removed answers  has an OPERATOR dealt with it?   — the `status` column
 *
 * A removed agent is silent forever by construction — its node is gone and its Atlas
 * addresses have been reclaimed. Counting it as an attention item means the fleet can
 * never be brought to a clean state: the work is done and the badge still says do it.
 * So `removed` wins over every silence-derived colour, count and action here, and it
 * is surfaced as its own quiet count instead of being hidden — the rows still exist
 * and an operator auditing what was reclaimed must be able to find them.
 *
 * Everything is a pure function of (agent, now) so `scripts/check-agent-status.mjs`
 * can pin the boundaries without a browser. The thresholds themselves stay in
 * `@infras/shared-types` — nothing here restates a number.
 */

/** The shape every derivation needs — the fleet row, narrowed. */
export interface FleetAgentLike {
  status: string;
  lastSeenAt?: string | null;
}

/**
 * Dealt with. The only status that means "a human has already resolved this agent":
 * `removal_failed` is emphatically NOT one — it means the cascade tried and Atlas said
 * no, so the address is still held and the row still needs someone.
 */
export function isRemovedAgent(a: FleetAgentLike): boolean {
  return a.status === 'removed';
}

/** Red: silent past the red threshold, and nobody has dealt with it. This is THE
 *  verdict — what `?status=__red__` selects, what the Overview checklist counts, what
 *  carries a bulk checkbox, and what the remove dialog pre-ticks. One question, one
 *  answer, on every surface. */
export function isRedAgentRow(a: FleetAgentLike, now?: Date): boolean {
  return !isRemovedAgent(a) && !!a.lastSeenAt && agentStaleLevel(a.lastSeenAt, now) === 'red';
}

/** The warning colour for a row: `green` for a removed agent, because the colour means
 *  "someone should look at this" and nobody should. */
export function agentLevel(a: FleetAgentLike, now?: Date): AgentStaleLevel {
  if (isRemovedAgent(a) || !a.lastSeenAt) return 'green';
  return agentStaleLevel(a.lastSeenAt, now);
}

/** Row tint. Green — and removed — is the ABSENCE of a class. */
export function agentRowClass(a: FleetAgentLike, now?: Date): string | undefined {
  const level = agentLevel(a, now);
  return level === 'green' ? undefined : `agent-row-${level}`;
}

/**
 * Can this row be reclaimed? A removed agent holds nothing (its link rows are
 * released), so offering Remove on it is an action with no effect — and, on the red
 * filter, the exact trap the first bulk run walked into.
 *
 * This is UX, not enforcement: the server re-classifies every target inside the
 * confirm call.
 */
export function isRemovableAgent(a: FleetAgentLike): boolean {
  return !isRemovedAgent(a);
}

/**
 * Which rows get a bulk checkbox: the red ones, and only those.
 *
 * This used to be `gone || red`, because bulk selection was originally keyed on the
 * 4-hour reclaim clock. Operating the shipped feature is what killed that clock: a
 * DOKS upgrade left 8 dead node addresses and, with the nodes only 30–60 min silent,
 * neither bulk affordance was offered and the reclaim took 8 individual dialogs. The
 * droplets were provably deleted; the UI was waiting out a clock that answered a
 * different question, on a timescale no operator was willing to wait for.
 *
 * A red row is selectable. That does NOT make it reclaimable — nothing here deletes
 * anything. Selecting only opens the preview; the server's `removable` stays the
 * selection authority inside it, and a live node's own address still arrives unticked
 * and warned. This decides who may be LOOKED at, not what may be deleted.
 *
 * `removed` is excluded (its link rows are already released, so the action has no
 * effect). `removal_failed` is included: Atlas refused the delete, the address is
 * still held, and the retry is exactly the bulk path.
 */
export function isBulkSelectable(a: FleetAgentLike, now?: Date): boolean {
  return isRedAgentRow(a, now);
}

/** One cluster's counts. `live + removed = total`, and `active + pending + red +
 *  other = live` — the buckets partition the cluster or they teach the reader to
 *  distrust all of them. `red` is both the attention count and the selectable count;
 *  there is no longer a second, wider set to track beside it. */
export interface FleetSummary {
  total: number;
  live: number;
  removed: number;
  active: number;
  pending: number;
  red: number;
  other: number;
}

export function summarizeAgents(rows: FleetAgentLike[], now?: Date): FleetSummary {
  const removed = rows.filter(isRemovedAgent).length;
  const live = rows.length - removed;
  const red = rows.filter((a) => isRedAgentRow(a, now)).length;
  const active = rows.filter(
    (a) => a.status === 'active' && !isRemovedAgent(a) && !isRedAgentRow(a, now),
  ).length;
  const pending = rows.filter(
    (a) => a.status === 'pending_removal' && !isRedAgentRow(a, now),
  ).length;
  return {
    total: rows.length,
    live,
    removed,
    active,
    pending,
    red,
    other: live - active - pending - red,
  };
}
